import { createError } from '@common/neo.tax';

import { CompanyScope, OrganizationScope } from '../graphql';
import { OrganizationInvitation, OrganizationMember } from '../hooks/useCurrentUser';
import {
  OrganizationMemberItemScope,
  OrganizationMembersTableItem,
  CompanyInvitation,
  CompanyIdAndShortName,
  OrganizationMembersTableItemInvitation,
} from '../types';

export const companyMemberItemScopeDisplayName: Record<OrganizationMemberItemScope, string> = {
  [OrganizationMemberItemScope.ADMIN]: 'Admin',
  [OrganizationMemberItemScope.MEMBER]: 'Member',
  [OrganizationMemberItemScope.NON_FINANCIAL]: 'Non-Financial',
};

const organizationMemberScope = (member: OrganizationMember): OrganizationMemberItemScope => {
  switch (member.organizationScope) {
    case OrganizationScope.OrganizationAdmin:
      return OrganizationMemberItemScope.ADMIN;
    case OrganizationScope.OrganizationMember:
      if (member.companyScopes.length === 0) {
        // If an organization member has no company scopes, default to NON_FINANCIAL
        // This handles edge cases where data might be inconsistent
        return OrganizationMemberItemScope.NON_FINANCIAL;
      }
      if (member.companyScopes[0].scope === CompanyScope.CompanyMember) {
        return OrganizationMemberItemScope.MEMBER;
      }
      return OrganizationMemberItemScope.NON_FINANCIAL;
    default:
      throw createError('ScopeNotFound', 'Organization scope not found for member. This should never happen.');
  }
};

const organizationInvitationScope = (
  organizationInvitation: OrganizationInvitation,
  companyInvitations: CompanyInvitation[],
): OrganizationMemberItemScope => {
  const companyInvitationsForTheOrgMember = companyInvitations.filter(
    (companyInvitation) => companyInvitation.email === organizationInvitation.email,
  );
  switch (organizationInvitation.scope) {
    case OrganizationScope.OrganizationAdmin:
      return OrganizationMemberItemScope.ADMIN;
    case OrganizationScope.OrganizationMember:
      if (companyInvitationsForTheOrgMember[0]?.scope === CompanyScope.CompanyMember) {
        return OrganizationMemberItemScope.MEMBER;
      }
      return OrganizationMemberItemScope.NON_FINANCIAL;
    default:
      throw createError('ScopeNotFound', 'Organization scope not found for invitation.');
  }
};

export const toOrganizationMemberTableItem = (
  member: OrganizationMember,
  companies: CompanyIdAndShortName[],
): OrganizationMembersTableItem => {
  const companiesWithScope =
    member.organizationScope === OrganizationScope.OrganizationAdmin
      ? companies
      : member.companyScopes
          .map((companyScope) => companies.find((company) => company.id === companyScope.companyId))
          .filter((company): company is CompanyIdAndShortName => !!company);
  return {
    id: member.id,
    email: member.email,
    fullName: member.fullName,
    companies: companiesWithScope,
    scope: organizationMemberScope(member),
    type: 'ORGANIZATION_MEMBER',
  };
};

const companyScopeToCompanyMemberItemScope = (companyScope: CompanyScope): OrganizationMemberItemScope => {
  switch (companyScope) {
    case CompanyScope.CompanyMember:
      return OrganizationMemberItemScope.MEMBER;
    case CompanyScope.NonFinancial:
      return OrganizationMemberItemScope.NON_FINANCIAL;
    default:
      throw createError('ScopeNotFound', `Unknown company scope: ${companyScope}.`);
  }
};

export const organizationInvitationToOrganizationMemberTableItem = (
  organizationInvitation: OrganizationInvitation,
  companyInvitations: CompanyInvitation[],
  companies: CompanyIdAndShortName[],
): OrganizationMembersTableItem => {
  const scope = organizationInvitationScope(organizationInvitation, companyInvitations);

  if (organizationInvitation.scope === OrganizationScope.OrganizationAdmin) {
    return {
      id: organizationInvitation.id,
      email: organizationInvitation.email,
      fullName: organizationInvitation.email,
      companies,
      scope: organizationInvitationScope(organizationInvitation, companyInvitations),
      derivedState: organizationInvitation.derivedState as OrganizationMembersTableItemInvitation['derivedState'],
      expiresAt: organizationInvitation.expiresAt,
      revokedAt: organizationInvitation.revokedAt,
      acceptedAt: organizationInvitation.acceptedAt,
      type: 'INVITED_ORGANIZATION_MEMBER',
    };
  }

  const companiesWithAccess = companyInvitations
    .filter((companyInvitation) => companyInvitation.email === organizationInvitation.email)
    .map((companyInvitation) => {
      const company = companies.find((company) => company.id === companyInvitation.companyId);
      if (!company) {
        throw createError('CompanyNotFound', 'Company not found.');
      }
      return company;
    });

  return {
    id: organizationInvitation.id,
    email: organizationInvitation.email,
    fullName: organizationInvitation.email,
    companies: companiesWithAccess,
    scope,
    derivedState: organizationInvitation.derivedState as OrganizationMembersTableItemInvitation['derivedState'],
    expiresAt: organizationInvitation.expiresAt,
    revokedAt: organizationInvitation.revokedAt,
    acceptedAt: organizationInvitation.acceptedAt,
    type: 'INVITED_ORGANIZATION_MEMBER',
  };
};

export const companyInvitationToCompanyMembersTableItem = (
  companyInvitation: CompanyInvitation,
  companies: CompanyIdAndShortName[],
): OrganizationMembersTableItem => {
  const company = companies.find((company) => company.id === companyInvitation.companyId);
  if (!company) {
    throw createError('CompanyNotFound', 'Company not found.');
  }
  return {
    id: companyInvitation.id,
    email: companyInvitation.email,
    fullName: companyInvitation.email,
    companies: [company],
    scope: companyScopeToCompanyMemberItemScope(companyInvitation.scope),
    // Child CompanyInvitation rows inherit their lifecycle from the parent org invitation
    // and don't carry derivedState on the wire; acceptance is the only per-child signal.
    acceptedAt: companyInvitation.acceptedAt,
    type: 'INVITED_ORGANIZATION_MEMBER',
  };
};

export const isInvitation = (member: OrganizationMembersTableItem): member is OrganizationMembersTableItemInvitation =>
  member.type === 'INVITED_ORGANIZATION_MEMBER';
